import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import Chart from 'chart.js/auto';
import { ApiError } from '../lib/api';
import {
  categoriseFailure,
  defaultCustomRange,
  FAIL_CATEGORIES,
  fetchReport,
  fetchStops,
  formatDateAU,
  formatPeriodLabel,
  pct,
  periodKey,
  rangeToDates,
  STATE_COLOURS,
  type DateRange,
  type DifotReport,
  type DifotStop,
  type Filters,
  type Period,
} from '../lib/difot';
import '../difot.css';

const RANGE_LABELS: Record<DateRange, string> = {
  all: 'All available',
  today: 'Today',
  yesterday: 'Yesterday',
  last7: 'Last 7 days',
  last30: 'Last 30 days',
  custom: 'Custom range',
};

/** Switching to a custom window seeds the inputs from the window already on screen. */
function applyRange(filters: Filters, range: DateRange): Filters {
  if (range !== 'custom') return { ...filters, range };
  return { ...filters, range, ...defaultCustomRange(filters.range, filters) };
}

/** Header caption for the active window — custom ranges spell out the dates. */
function windowLabel(filters: Filters): string {
  if (filters.range !== 'custom') return RANGE_LABELS[filters.range];
  const { from, to } = rangeToDates('custom', filters);
  if (!from && !to) return 'Custom range';
  if (from && to) return from === to ? formatDateAU(from) : `${formatDateAU(from)} – ${formatDateAU(to)}`;
  return from ? `From ${formatDateAU(from)}` : `Up to ${formatDateAU(to)}`;
}

export function Difot() {
  const [filters, setFilters] = useState<Filters>({ range: 'yesterday', state: '', drivers: [], status: '' });
  const [period, setPeriod] = useState<Period>('daily');
  const [report, setReport] = useState<DifotReport | null>(null);
  const [failures, setFailures] = useState<DifotStop[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // A half-filled custom range would query an open-ended window, so hold off
  // until both ends are set rather than flashing an unintended report.
  const awaitingCustomDates = filters.range === 'custom' && (!filters.from || !filters.to);

  const load = useCallback(async () => {
    if (filters.range === 'custom' && (!filters.from || !filters.to)) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // Windows are anchored to the live business day, so a single windowed
      // fetch is enough — no all-time pre-fetch or latest-day anchor needed.
      const rep = await fetchReport(filters);
      // Render the report immediately — the failure list is secondary and must
      // not be able to blank the whole dashboard if it fails.
      setReport(rep);
    } catch (err) {
      console.error('DIFOT report load failed', err);
      setError(
        err instanceof ApiError
          ? `Could not load the DIFOT report: ${err.message} (${err.status})`
          : err instanceof Error
            ? `Could not load the DIFOT report: ${err.message}`
            : 'Failed to load the DIFOT report',
      );
      setLoading(false);
      return;
    }

    try {
      const { from, to } = rangeToDates(filters.range, filters);
      const fails = await fetchStops({
        from,
        to,
        state: filters.state,
        drivers: filters.drivers,
        status: 'Failed',
        limit: 600,
      });
      setFailures(fails);
    } catch {
      setFailures([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="difot">
      <Header label={windowLabel(filters)} loading={loading} onRefresh={() => void load()} />

      {error && (
        <p className="difot-error" role="alert">
          {error}
        </p>
      )}

      <FilterBar
        filters={filters}
        period={period}
        options={report?.options ?? { states: [], drivers: [] }}
        onChange={setFilters}
        onPeriod={setPeriod}
      />

      {awaitingCustomDates && (
        <p className="difot-hint">Pick a start and end date to run the report over a custom window.</p>
      )}

      {report && (
        <>
          <Kpis report={report} />
          <section className="card difot-section">
            <div className="difot-section-head">
              <div>
                <div className="difot-section-title">DIFOT Trend</div>
                <div className="difot-section-sub">
                  Deliveries completed as a share of attempted stops, across the selected period
                </div>
              </div>
              <Legend
                items={[
                  { c: '#0bda8e', t: 'DIFOT %' },
                  { c: 'rgba(0,97,162,0.5)', t: 'Stops' },
                  { c: '#ff5245', t: 'Failed' },
                ]}
              />
            </div>
            <TrendChart report={report} period={period} />
          </section>

          <div className="difot-grid cols-3">
            <section className="card difot-section">
              <div className="difot-section-head">
                <div>
                  <div className="difot-section-title">Performance by State</div>
                  <div className="difot-section-sub">Volume and DIFOT % by delivery state</div>
                </div>
              </div>
              <StateChart report={report} />
              <StateCards report={report} />
            </section>

            <FailurePanel failures={failures} />
          </div>

          <section className="card difot-section">
            <div className="difot-section-head">
              <div>
                <div className="difot-section-title">Driver / Run Leaderboard</div>
                <div className="difot-section-sub">Ranked by attempted deliveries</div>
              </div>
            </div>
            <DriverTable report={report} />
          </section>

          <StopLookup />
        </>
      )}

      {loading && !report && <p className="muted">Loading DIFOT report…</p>}
    </div>
  );
}

// ─── Header ─────────────────────────────────────────────────────────────────

function Header({
  label,
  loading,
  onRefresh,
}: {
  label: string;
  loading: boolean;
  onRefresh: () => void;
}) {
  return (
    <header className="difot-head">
      <div className="difot-brand">
        <div className="difot-logo">BL</div>
        <div>
          <div className="difot-eyebrow">Bing Lee · Powered by Locate2u</div>
          <h1>DIFOT Performance Dashboard</h1>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <div className="difot-head-meta">
          <div>
            Window <span className="strong">{label}</span>
          </div>
          <div>{loading ? 'Refreshing…' : 'Up to date'}</div>
        </div>
        <button type="button" className="difot-btn" onClick={onRefresh} disabled={loading}>
          Refresh
        </button>
      </div>
    </header>
  );
}

// ─── Filters ──────────────────────────────────────────────────────────────

function FilterBar({
  filters,
  period,
  options,
  onChange,
  onPeriod,
}: {
  filters: Filters;
  period: Period;
  options: { states: string[]; drivers: string[] };
  onChange: (f: Filters) => void;
  onPeriod: (p: Period) => void;
}) {
  return (
    <div className="card difot-filters">
      <div className="difot-field">
        <label>Trend period</label>
        <div className="difot-seg">
          {(['daily', 'weekly', 'monthly'] as Period[]).map((p) => (
            <button
              key={p}
              type="button"
              className={period === p ? 'active' : ''}
              onClick={() => onPeriod(p)}
            >
              {p[0].toUpperCase() + p.slice(1)}
            </button>
          ))}
        </div>
      </div>
      <div className="difot-field">
        <label>Date range</label>
        <select value={filters.range} onChange={(e) => onChange(applyRange(filters, e.target.value as DateRange))}>
          <option value="all">All available</option>
          <option value="today">Today</option>
          <option value="yesterday">Yesterday</option>
          <option value="last7">Last 7 days</option>
          <option value="last30">Last 30 days</option>
          <option value="custom">Custom range…</option>
        </select>
      </div>
      {filters.range === 'custom' && (
        <div className="difot-field difot-field-wide">
          <label>Custom dates</label>
          <div className="difot-dates">
            <input
              type="date"
              aria-label="Start date"
              value={filters.from ?? ''}
              max={filters.to || undefined}
              onChange={(e) => onChange({ ...filters, from: e.target.value })}
            />
            <span className="difot-dates-sep">to</span>
            <input
              type="date"
              aria-label="End date"
              value={filters.to ?? ''}
              min={filters.from || undefined}
              onChange={(e) => onChange({ ...filters, to: e.target.value })}
            />
          </div>
        </div>
      )}
      <div className="difot-field">
        <label>State</label>
        <select value={filters.state} onChange={(e) => onChange({ ...filters, state: e.target.value })}>
          <option value="">All states</option>
          {options.states.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>
      <div className="difot-field difot-field-wide">
        <label id="difot-driver-label">Driver / Run</label>
        <MultiSelect
          labelledBy="difot-driver-label"
          allLabel="All drivers"
          noun="driver"
          options={options.drivers}
          selected={filters.drivers}
          onChange={(drivers) => onChange({ ...filters, drivers })}
        />
      </div>
      <div className="difot-field">
        <label>Status</label>
        <select value={filters.status} onChange={(e) => onChange({ ...filters, status: e.target.value })}>
          <option value="">All statuses</option>
          <option value="Complete">Complete</option>
          <option value="Failed">Failed</option>
          <option value="Pending">Pending</option>
          <option value="Cancelled">Cancelled</option>
        </select>
      </div>
    </div>
  );
}

/**
 * Checkbox dropdown for filters that accept several values at once. An empty
 * selection means "everything", matching how the API treats a missing filter.
 */
function MultiSelect({
  labelledBy,
  allLabel,
  noun,
  options,
  selected,
  onChange,
}: {
  labelledBy: string;
  allLabel: string;
  /** Singular noun used in the summary, e.g. "driver" → "3 drivers selected". */
  noun: string;
  options: string[];
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement | null>(null);

  // Anything still selected but no longer offered (e.g. a driver with no stops
  // in the current data) stays listed, so it can be unticked.
  const all = useMemo(() => {
    const known = new Set(options);
    return [...options, ...selected.filter((s) => !known.has(s))];
  }, [options, selected]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? all.filter((o) => o.toLowerCase().includes(q)) : all;
  }, [all, query]);

  // Clicking away or pressing Escape closes the panel.
  useEffect(() => {
    if (!open) return;
    const onPointer = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggle = (value: string) =>
    onChange(selected.includes(value) ? selected.filter((s) => s !== value) : [...selected, value]);

  const summary =
    selected.length === 0
      ? allLabel
      : selected.length === 1
        ? selected[0]
        : `${selected.length} ${noun}s selected`;

  return (
    <div className="difot-ms" ref={rootRef}>
      <button
        type="button"
        className="difot-ms-btn"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-labelledby={labelledBy}
        onClick={() => setOpen((o) => !o)}
      >
        <span className={selected.length ? 'difot-ms-summary strong' : 'difot-ms-summary muted'}>{summary}</span>
        <span aria-hidden="true" className="difot-ms-caret">
          ▾
        </span>
      </button>

      {open && (
        <div className="difot-ms-panel">
          <input
            className="difot-ms-search"
            type="search"
            value={query}
            placeholder={`Search ${noun}s…`}
            aria-label={`Search ${noun}s`}
            onChange={(e) => setQuery(e.target.value)}
          />
          <div className="difot-ms-actions">
            <button type="button" onClick={() => onChange(visible)} disabled={visible.length === 0}>
              Select {query ? 'shown' : 'all'}
            </button>
            <button type="button" onClick={() => onChange([])} disabled={selected.length === 0}>
              Clear
            </button>
          </div>
          <div className="difot-ms-list" role="listbox" aria-multiselectable="true">
            {visible.length === 0 && <div className="difot-ms-empty">No matches</div>}
            {visible.map((o) => (
              <label className="difot-ms-opt" key={o}>
                <input type="checkbox" checked={selected.includes(o)} onChange={() => toggle(o)} />
                <span>{o}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {selected.length > 1 && (
        <div className="difot-ms-chips">
          {selected.map((s) => (
            <button
              type="button"
              className="difot-ms-chip"
              key={s}
              title={`Remove ${s}`}
              onClick={() => toggle(s)}
            >
              {s} <span aria-hidden="true">×</span>
              <span className="difot-sr-only">Remove {s}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Legend({ items }: { items: { c: string; t: string }[] }) {
  return (
    <div style={{ display: 'flex', gap: '0.85rem', fontSize: 11, color: '#737474' }}>
      {items.map((i) => (
        <span key={i.t} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <span className="difot-dot" style={{ background: i.c }} />
          {i.t}
        </span>
      ))}
    </div>
  );
}

// ─── KPIs ───────────────────────────────────────────────────────────────────

function Kpis({ report }: { report: DifotReport }) {
  const t = report.totals;
  const cards = [
    {
      label: 'DIFOT',
      value: `${pct(t.completed, t.denom).toFixed(1)}%`,
      sub: `${t.completed.toLocaleString()} of ${t.denom.toLocaleString()} attempted`,
      color: '#0bda8e',
    },
    {
      label: 'On Time',
      value: `${pct(t.in_window_n, t.completed_with_window).toFixed(1)}%`,
      sub: `of ${t.completed_with_window.toLocaleString()} with a window`,
      color: '#1a85c9',
    },
    {
      label: 'Failed',
      value: t.failed.toLocaleString(),
      sub: `${pct(t.failed, t.denom).toFixed(1)}% failure rate`,
      color: '#ff5245',
    },
    {
      label: 'Pending',
      value: t.pending.toLocaleString(),
      sub: `${pct(t.pending, t.denom).toFixed(1)}% of attempted`,
      color: '#b37d6c',
    },
    {
      // Cancelled stops sit outside `denom`, so the sub-line is a share of every
      // stop on the plan — quoting it against "attempted" would be wrong.
      label: 'Cancelled',
      value: t.cancelled.toLocaleString(),
      sub: `${pct(t.cancelled, t.total).toFixed(1)}% of ${t.total.toLocaleString()} stops`,
      color: '#737474',
    },
    {
      label: 'Avg Service',
      value: t.avg_service != null ? `${t.avg_service.toFixed(1)}m` : '—',
      sub: `${t.drivers} active drivers`,
      color: '#003d6c',
    },
  ];
  return (
    <div className="difot-kpis">
      {cards.map((c) => (
        <div className="difot-kpi" key={c.label}>
          <div className="accent" style={{ background: c.color }} />
          <div className="k-label">{c.label}</div>
          <div className="k-value num" style={{ color: c.color }}>
            {c.value}
          </div>
          <div className="k-sub">{c.sub}</div>
        </div>
      ))}
    </div>
  );
}

// ─── Trend chart ──────────────────────────────────────────────────────────

function TrendChart({ report, period }: { report: DifotReport; period: Period }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const chartRef = useRef<Chart | null>(null);

  const buckets = useMemo(() => {
    const map = new Map<string, { total: number; failed: number; completed: number; denom: number }>();
    for (const d of report.daily) {
      const k = periodKey(d.d, period);
      const b = map.get(k) ?? { total: 0, failed: 0, completed: 0, denom: 0 };
      b.total += d.total;
      b.failed += d.failed;
      b.completed += d.completed;
      b.denom += d.denom;
      map.set(k, b);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [report, period]);

  useEffect(() => {
    if (!canvasRef.current) return;
    const labels = buckets.map(([k]) => formatPeriodLabel(k, period));
    const difotData = buckets.map(([, b]) => (b.denom ? +pct(b.completed, b.denom).toFixed(1) : null));
    const volData = buckets.map(([, b]) => b.total);
    const failData = buckets.map(([, b]) => b.failed);

    chartRef.current?.destroy();
    chartRef.current = new Chart(canvasRef.current, {
      type: 'bar',
      data: {
        labels,
        datasets: [
          {
            type: 'bar',
            label: 'Stops',
            data: volData,
            backgroundColor: 'rgba(0,97,162,0.22)',
            borderColor: 'rgba(0,97,162,0.5)',
            borderWidth: 1,
            yAxisID: 'y2',
            borderRadius: 6,
            order: 3,
          },
          {
            type: 'bar',
            label: 'Failed',
            data: failData,
            backgroundColor: 'rgba(255,82,69,0.75)',
            yAxisID: 'y2',
            borderRadius: 6,
            order: 2,
          },
          {
            type: 'line',
            label: 'DIFOT %',
            data: difotData,
            borderColor: '#0bda8e',
            backgroundColor: 'rgba(11,218,142,0.12)',
            borderWidth: 3,
            pointRadius: 4,
            pointBackgroundColor: '#0bda8e',
            pointBorderColor: '#fff',
            pointBorderWidth: 2,
            fill: true,
            tension: 0.35,
            yAxisID: 'y',
            order: 1,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: { display: false },
          tooltip: { backgroundColor: '#003d6c', padding: 10, cornerRadius: 6 },
        },
        scales: {
          x: { grid: { display: false }, ticks: { color: '#737474', font: { size: 11 } } },
          y: {
            position: 'left',
            min: 0,
            max: 100,
            ticks: { callback: (v) => `${v}%`, color: '#737474', font: { size: 11 } },
            grid: { color: '#f1f3f7' },
          },
          y2: {
            position: 'right',
            beginAtZero: true,
            ticks: { color: '#737474', font: { size: 11 } },
            grid: { display: false },
          },
        },
      },
    });
    return () => {
      chartRef.current?.destroy();
      chartRef.current = null;
    };
  }, [buckets, period]);

  return (
    <div className="difot-chart">
      <canvas ref={canvasRef} />
    </div>
  );
}

// ─── State chart + cards ────────────────────────────────────────────────────

function StateChart({ report }: { report: DifotReport }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const chartRef = useRef<Chart | null>(null);

  useEffect(() => {
    if (!canvasRef.current) return;
    const rows = report.by_state;
    chartRef.current?.destroy();
    chartRef.current = new Chart(canvasRef.current, {
      type: 'bar',
      data: {
        labels: rows.map((r) => r.state),
        datasets: [
          { label: 'Delivered', data: rows.map((r) => r.complete), backgroundColor: '#0bda8e', stack: 'a', borderRadius: 4 },
          { label: 'Failed', data: rows.map((r) => r.failed), backgroundColor: '#ff5245', stack: 'a' },
          { label: 'Pending', data: rows.map((r) => r.pending), backgroundColor: '#b37d6c', stack: 'a', borderRadius: 4 },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: 'y',
        plugins: {
          legend: {
            position: 'bottom',
            labels: { usePointStyle: true, pointStyle: 'rectRounded', boxWidth: 8, padding: 14, color: '#737474', font: { size: 11 } },
          },
          tooltip: { backgroundColor: '#003d6c', padding: 10, cornerRadius: 6 },
        },
        scales: {
          x: { stacked: true, ticks: { color: '#737474', font: { size: 11 } }, grid: { color: '#f1f3f7' } },
          y: { stacked: true, ticks: { color: '#384553', font: { size: 12, weight: 600 } }, grid: { display: false } },
        },
      },
    });
    return () => {
      chartRef.current?.destroy();
      chartRef.current = null;
    };
  }, [report]);

  return (
    <div className="difot-chart short">
      <canvas ref={canvasRef} />
    </div>
  );
}

function StateCards({ report }: { report: DifotReport }) {
  return (
    <div className="difot-state-cards">
      {report.by_state.map((s) => {
        const p = pct(s.complete, s.denom);
        const colour = STATE_COLOURS[s.state] ?? '#0061a2';
        return (
          <div className="difot-state-card" key={s.state}>
            <div className="top">
              <span className="name" style={{ color: colour }}>
                {s.state}
              </span>
              <span className="vol">{s.total.toLocaleString()} stops</span>
            </div>
            <div className="big num">{p.toFixed(1)}%</div>
            <div className="difot-bar">
              <div style={{ width: `${Math.min(p, 100)}%`, background: colour }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Failure panel ────────────────────────────────────────────────────────

function FailurePanel({ failures }: { failures: DifotStop[] | null }) {
  const categories = useMemo(() => {
    const counts = new Map<string, number>();
    for (const f of failures ?? []) {
      const key = categoriseFailure(f.fail_note);
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return FAIL_CATEGORIES.map((c) => ({ ...c, count: counts.get(c.key) ?? 0 })).filter((c) => c.count > 0);
  }, [failures]);

  const total = failures?.length ?? 0;

  return (
    <section className="card difot-section">
      <div className="difot-section-head">
        <div>
          <div className="difot-section-title">Failure Reasons</div>
          <div className="difot-section-sub">From driver attempt notes</div>
        </div>
        <span className="difot-pill" style={{ background: '#ffebe9', color: '#ff5245' }}>
          <span className="difot-dot" style={{ background: '#ff5245' }} />
          {total}
        </span>
      </div>

      {categories.length > 0 && (
        <div className="difot-cat" style={{ marginBottom: '1rem' }}>
          {categories
            .sort((a, b) => b.count - a.count)
            .map((c) => {
              const p = total ? (c.count / total) * 100 : 0;
              return (
                <div className="difot-cat-row" key={c.key}>
                  <div>
                    <div className="lab">{c.label}</div>
                    <div className="difot-bar">
                      <div style={{ width: `${p}%`, background: c.color }} />
                    </div>
                  </div>
                  <div className="cnt" style={{ color: c.color }}>
                    {c.count}
                  </div>
                </div>
              );
            })}
        </div>
      )}

      <div className="difot-scroll">
        {failures && failures.length === 0 && (
          <div className="difot-empty">No failed deliveries for the selected filters</div>
        )}
        {(failures ?? []).slice(0, 100).map((f) => (
          <div className="difot-fail" key={f.stop_id}>
            <div className="row1">
              <span className="so">{f.so || f.ref || `Stop ${f.stop_id}`}</span>
              {f.state && (
                <span
                  className="difot-pill"
                  style={{ background: `${STATE_COLOURS[f.state] ?? '#eaf2fb'}22`, color: STATE_COLOURS[f.state] ?? '#384553' }}
                >
                  {f.state}
                </span>
              )}
            </div>
            <div className="meta">
              {(f.driver || 'Unassigned') + ' · ' + formatDateAU(f.trip_date)}
            </div>
            <div className="note">“{(f.fail_note || 'No reason recorded').slice(0, 200)}”</div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ─── Driver leaderboard ─────────────────────────────────────────────────────

function DriverTable({ report }: { report: DifotReport }) {
  return (
    <div className="difot-tablewrap">
      <table className="difot-table">
        <thead>
          <tr>
            <th className="num">#</th>
            <th>Driver / Run</th>
            <th>Team</th>
            <th className="num">Attempted</th>
            <th className="num">DIFOT %</th>
            <th className="num">On Time %</th>
            <th className="num">Failed</th>
            <th className="num">Avg Service</th>
          </tr>
        </thead>
        <tbody>
          {report.drivers.map((d, i) => (
            <tr key={d.driver}>
              <td className="num difot-rank">{i + 1}</td>
              <td>{d.driver}</td>
              <td className="muted">{d.team ?? '—'}</td>
              <td className="num">{d.denom.toLocaleString()}</td>
              <td className="num" style={{ fontWeight: 700, color: '#003d6c' }}>
                {pct(d.completed, d.denom).toFixed(1)}%
              </td>
              <td className="num">{d.with_window ? `${pct(d.in_window_n, d.with_window).toFixed(1)}%` : '—'}</td>
              <td className="num" style={{ color: d.failed ? '#ff5245' : undefined }}>
                {d.failed}
              </td>
              <td className="num">{d.avg_service != null ? `${d.avg_service.toFixed(1)}m` : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Stop lookup (search by sales order / tracking reference) ───────────────

function StopLookup() {
  const [q, setQ] = useState('');
  const [results, setResults] = useState<DifotStop[] | null>(null);
  const [busy, setBusy] = useState(false);

  async function run(e: FormEvent) {
    e.preventDefault();
    const term = q.trim();
    if (!term) return;
    setBusy(true);
    try {
      setResults(await fetchStops({ q: term, limit: 25 }));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card difot-section" style={{ marginTop: '1.25rem' }}>
      <div className="difot-section-head">
        <div>
          <div className="difot-section-title">Stop Lookup</div>
          <div className="difot-section-sub">Search by sales order (SO…) or tracking reference</div>
        </div>
      </div>
      <form onSubmit={run} className="difot-search" style={{ display: 'flex', gap: '0.5rem', maxWidth: 420 }}>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="e.g. SO3978005"
          aria-label="Search sales order or reference"
        />
        <button type="submit" className="difot-btn" disabled={busy}>
          {busy ? '…' : 'Search'}
        </button>
      </form>

      {results && (
        <div className="difot-tablewrap" style={{ marginTop: '1rem' }}>
          {results.length === 0 ? (
            <div className="difot-empty">No matching stops</div>
          ) : (
            <table className="difot-table">
              <thead>
                <tr>
                  <th>Sales Order</th>
                  <th>Status</th>
                  <th>Driver / Run</th>
                  <th>State</th>
                  <th>Date</th>
                  <th>Window</th>
                  <th>On time</th>
                </tr>
              </thead>
              <tbody>
                {results.map((r) => (
                  <tr key={r.stop_id}>
                    <td style={{ fontWeight: 600 }}>{r.so || r.ref}</td>
                    <td>{r.status}</td>
                    <td>{r.driver ?? '—'}</td>
                    <td>{r.state ?? '—'}</td>
                    <td>{formatDateAU(r.trip_date)}</td>
                    <td className="muted">{r.w_start ? `${r.w_start}–${r.w_end}` : '—'}</td>
                    <td>{r.in_window == null ? '—' : r.in_window ? '✓' : '✗'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </section>
  );
}
